import React, { useState } from "react";
import withLayout from "../../hocs/Layout";
import { getMerchant } from '../../helpers/ReduxHelpers';
import { apiConstants, postGeneric } from '../../common/constants';
import { useFieldArray, useForm } from "react-hook-form";
import RateList from "./rateList";
import { Box, Grid, Typography, InputAdornment, IconButton, Button, TextField } from "@mui/material";
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import AddIcon from "@mui/icons-material/Add";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { FormProvider, RHFRadioGroup, RHFTextField } from "../../common/hook-form";
import { LoadingButton } from "@mui/lab";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from 'yup'
import _ from 'lodash'
import PageTitle from "../../common/PageTitle";
import { useSnackbar } from "notistack"
import { FONT, SIZE, WEIGHT } from '../../styles/foundation/typography';
import { NAVY, BORDER, SUBTLE, WHITE, PRIMARY_BLUE, LIGHT_BG, ERROR } from '../../styles/foundation/colors';
import { SHADOW } from '../../styles/foundation/effects';
import { pageContainerSx, pageHeroSx, heroTitleSx, heroSubSx } from '../../styles/patterns';

const cardSx = { bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px', boxShadow: SHADOW.card, p: { xs: 2, md: 2.5 } };
const sectionLabelSx = { fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.bold, color: SUBTLE, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1.5 };
const boxCardSx = { p: 1.75, mb: 1.5, border: `1px solid ${BORDER}`, borderRadius: '10px', bgcolor: LIGHT_BG };

const ShipmentRateCalculatorPTL = () => {
    const merchant = getMerchant();
    const merchantId = merchant.id ? merchant.id : ''

    const { enqueueSnackbar } = useSnackbar();

    const [isLoading, setLoading] = useState(false);
    const [rateListing, setRateList] = useState(null);

    const onSubmit = (data) => {
        let boxDetails = []
        if (data.list) {
            boxDetails = _.map(data.list, (item, index) => ({
                item_name: `Box${index + 1}`,
                units: item.count,
                additional: {
                    length: item.length,
                    breadth: item.breadth,
                    height: item.height,
                    weight: item.weight * 1000
                }
            }));
        }
        const totalWeight = _.sumBy(boxDetails, (item) => item.additional.weight * item.units);
        const totalLength = _.sumBy(boxDetails, (item) => item.additional.length * item.units);
        const totalBreadth = _.sumBy(boxDetails, (item) => item.additional.breadth * item.units);
        const totalHeight = _.sumBy(boxDetails, (item) => item.additional.height * item.units);
        setLoading(true);
        const isAppointmentBased = (data.isAppointmentBased === "true");
        const isInsured = (data.isInsured === "true")
        let payload = Object.assign(
            {},
            data,
            {
                "isMerchant": true,
                "merchantOid": merchantId,
                "shipmentType": 'B2B',
                "debug": true,
                "weight": data.weight * 1000,
                "invoiceValue": data.codAmount
            },
            {
                "codAmount": (data.paymentType === "COD") ? data.codAmount : 0,
                "boxDetails": boxDetails,
                "weight": totalWeight,
                "length": totalLength,
                "breadth": totalBreadth,
                "height": totalHeight,
                "isAppointmentBased": isAppointmentBased,
                "isInsured": isInsured,
            },
        )
        delete payload.list
        postGeneric(apiConstants.SHIPMENT_RATE_CALCULATOR, payload).then(response => {
            if (!response?.data[0]?.message) {
                setRateList(response?.data);
                enqueueSnackbar("Done", { variant: "success" })
            } else {
                enqueueSnackbar(response?.data[0]?.message, { variant: "error" })
            }
        })
            .catch((err) => {
                enqueueSnackbar(err.response?.data?.message, { variant: "error" });
            })
            .finally(() => setLoading(false))
    }

    const methods = useForm({
        resolver: yupResolver(
            Yup.object().shape({
                orderType: Yup.string().required("Order Type is required!"),
                paymentType: Yup.string().required("Payment Type is required!"),
                from: Yup.number().typeError('Pickup pincode must be valid').positive().typeError('Pickup pincode must be valid').required("Pickup pincode is required!"),
                to: Yup.number().typeError('Destination pincode must be valid').positive().typeError('Destination pincode must be valid').required("Destination pincode is required!"),
                codAmount: Yup.number().typeError('COD Amount must be valid').positive().typeError('COD Amount must be valid').required("COD Amount is required!"),
                list: Yup.array().of(
                    Yup.object().shape({
                        count: Yup.number().typeError('Box count must be valid').positive().typeError('Box count must be valid').required("Box count is required!"),
                        length: Yup.number().typeError('Shipment length must be valid').positive().typeError('Shipment length must be valid').required("Shipment length is required!"),
                        breadth: Yup.number().typeError('Shipment breadth must be valid').positive().typeError('Shipment breadth must be valid').required("Shipment breadth is required!"),
                        height: Yup.number().typeError('Shipment height must be valid').positive().typeError('Shipment height must be valid').required("Shipment height is required!"),
                        weight: Yup.number().typeError('Shipment weight must be valid').positive().typeError('Shipment weight must be valid').required("Shipment weight is required!"),
                    })
                ),
            })
        ),
        defaultValues: {
            paymentType: 'PREPAID',
            orderType: 'FORWARD',
            from: '160001',
            to: '250001',
            length: '',
            breadth: '',
            height: '',
            weight: '',
            codAmount: '100',
            isInsured: 'true',
            isAppointmentBased: "false",
            isCarrierRisk: 'false',
            codType: 'CASH',
            list: [
                { count: "", length: "", breadth: "", height: "", weight: "" },
            ],
        }
    })
    const { handleSubmit, control, register, watch } = methods;
    const { fields, append, remove } = useFieldArray({ control, name: "list" });

    const handleInputChange = (e) => {
        let input = e.target.value;
        input = input.replace(/[^0-9.]/g, '');
        const decimalCount = (input.match(/\./g) || []).length;
        if (decimalCount > 1) {
            input = input.slice(0, input.lastIndexOf('.'));
        }
        const parts = input.split('.');
        if (parts[1] && parts[1].length > 2) {
            input = `${parts[0]}.${parts[1].slice(0, 2)}`;
        }
        e.target.value = input;
    };

    const boxFieldSx = { flex: '1 1 120px' };

    return (
        <Box sx={pageContainerSx}>
            <PageTitle>PTL Rate Calculator</PageTitle>

            <Box sx={pageHeroSx}>
                <Typography sx={heroTitleSx}>PTL Rate Calculator</Typography>
                <Typography sx={heroSubSx}>Estimate PTL freight rates across carriers — enter the route, delivery options, and box dimensions.</Typography>
            </Box>

            <Box sx={cardSx}>
                <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                    {/* Route */}
                    <Typography sx={sectionLabelSx}>Route &amp; Value</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}><RHFTextField color="primary" name="from" label="Pickup Pincode *" size="small" /></Grid>
                        <Grid item xs={12} sm={4}><RHFTextField color="primary" name="to" label="Delivery Pincode *" size="small" /></Grid>
                        <Grid item xs={12} sm={4}><RHFTextField color="primary" name="codAmount" label="Invoice Value *" size="small" InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} /></Grid>
                    </Grid>

                    {/* Delivery options */}
                    <Typography sx={{ ...sectionLabelSx, mt: 2.5 }}>Delivery Options</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={6} sm={3} md={2}>
                            <RHFRadioGroup color="primary" label="Appointment Delivery *" name="isAppointmentBased" options={[{ label: 'No', value: "false" }, { label: 'Yes', value: "true" }]} />
                        </Grid>
                        <Grid item xs={6} sm={3} md={2}>
                            <RHFRadioGroup color="primary" label="Insured *" name="isInsured" options={[{ label: 'No', value: "false" }, { label: 'Yes', value: "true" }]} />
                        </Grid>
                        {watch("isInsured") === "false" && (
                            <Grid item xs={6} sm={3} md={2}>
                                <RHFRadioGroup color="primary" label="Risk Carrier *" name="isCarrierRisk" options={[{ label: 'Carrier', value: 'true' }, { label: 'Self', value: 'false' }]} />
                            </Grid>
                        )}
                        <Grid item xs={6} sm={3} md={2}>
                            <RHFRadioGroup color="primary" label="Payment Mode *" name="paymentType" options={[{ label: 'Prepaid', value: 'PREPAID' }, { label: 'COD', value: 'COD' }]} />
                        </Grid>
                        {watch("paymentType") === "COD" && (
                            <Grid item xs={12} sm={4} md={3}>
                                <RHFRadioGroup color="primary" label="COD Mode *" name="codType" options={[{ label: 'Cash', value: 'CASH' }, { label: 'Cheque', value: 'CHEQUE' }, { label: 'Draft', value: 'DRAFT' }]} />
                            </Grid>
                        )}
                    </Grid>

                    {/* Boxes */}
                    <Typography sx={{ ...sectionLabelSx, mt: 2.5 }}>Boxes</Typography>
                    {fields.map((field, index) => (
                        <Box key={field.id} sx={boxCardSx}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
                                <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: NAVY }}>Box {index + 1}</Typography>
                                {fields.length !== 1 && (
                                    <IconButton size="small" onClick={() => remove(index)} sx={{ color: ERROR }}>
                                        <RemoveCircleOutlineIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                <TextField sx={boxFieldSx} size="small" fullWidth variant="outlined" color="primary" type="number" label="No. of boxes" InputLabelProps={{ shrink: true }} name={`list[${index}].count`} {...register(`list[${index}].count`)} />
                                <TextField sx={boxFieldSx} size="small" fullWidth variant="outlined" color="primary" type="text" label="Weight" InputLabelProps={{ shrink: true }} InputProps={{ endAdornment: <InputAdornment position="end">kg</InputAdornment> }} name={`list[${index}].weight`} onInput={handleInputChange} {...register(`list[${index}].weight`)} />
                                <TextField sx={boxFieldSx} size="small" fullWidth variant="outlined" color="primary" type="text" label="Length" InputLabelProps={{ shrink: true }} InputProps={{ endAdornment: <InputAdornment position="end">cm</InputAdornment> }} name={`list[${index}].length`} onInput={handleInputChange} {...register(`list[${index}].length`)} />
                                <TextField sx={boxFieldSx} size="small" fullWidth variant="outlined" color="primary" type="text" label="Breadth" InputLabelProps={{ shrink: true }} InputProps={{ endAdornment: <InputAdornment position="end">cm</InputAdornment> }} name={`list[${index}].breadth`} onInput={handleInputChange} {...register(`list[${index}].breadth`)} />
                                <TextField sx={boxFieldSx} size="small" fullWidth variant="outlined" color="primary" type="text" label="Height" InputLabelProps={{ shrink: true }} InputProps={{ endAdornment: <InputAdornment position="end">cm</InputAdornment> }} name={`list[${index}].height`} onInput={handleInputChange} {...register(`list[${index}].height`)} />
                            </Box>
                        </Box>
                    ))}
                    <Button onClick={() => append({ count: "", length: "", breadth: "", height: "", weight: "" })}
                        startIcon={<AddIcon />} variant="outlined"
                        sx={{ fontFamily: FONT, textTransform: 'none', borderRadius: '8px', color: PRIMARY_BLUE, borderColor: PRIMARY_BLUE, mb: 1 }}>
                        Add another box
                    </Button>

                    {/* Submit */}
                    <Box sx={{ mt: 2.5 }}>
                        <LoadingButton loading={isLoading} type="submit" variant="contained" startIcon={<CalculateOutlinedIcon />}
                            sx={{ fontFamily: FONT, textTransform: 'none', borderRadius: '8px', bgcolor: PRIMARY_BLUE, py: 1.1, px: 4 }}>
                            Calculate Rates
                        </LoadingButton>
                    </Box>
                </FormProvider>
            </Box>

            {/* Results */}
            <Box sx={{ mt: 2.5 }}>
                {rateListing
                    ? <RateList rateListing={rateListing} />
                    : (
                        <Box sx={{ ...cardSx, minHeight: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                            <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: LIGHT_BG, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, color: SUBTLE, '& svg': { fontSize: 30 } }}>
                                <LocalShippingOutlinedIcon />
                            </Box>
                            <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY }}>No rates yet</Typography>
                            <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.5, maxWidth: 360 }}>
                                Fill in the route, delivery options and box details, then hit Calculate to compare PTL carrier rates.
                            </Typography>
                        </Box>
                    )}
            </Box>
        </Box>
    );
};

export default withLayout(ShipmentRateCalculatorPTL);
